import { reactive, toRaw } from 'vue'
import { PAGES } from './constants'

export const navigationStore = reactive({
  state: {
    smartbookAvailable: false,
    params: { tab: 'home', hidden: false } as Record<string, string | boolean>,
    currentLocation: window.location.href
  },

  setSmartbookAvailable(val: boolean) {
    this.state.smartbookAvailable = !!val
  },

  updateLocation() {
    this.state.currentLocation = window.location.href
  },

  readLocation() {
    const query = new URLSearchParams(window.location.search)
    for (const [key, value] of Object.entries(this.state.params)) {
      if (!query.has(key)) continue

      let newValue: string | boolean | null = query.get(key)
      if (typeof value === 'boolean')
        newValue = ({ false: false, true: true } as Record<string, boolean>)[newValue as string]

      this.state.params[key] = newValue as string | boolean
    }

    const pageExists = (PAGES as Array<{ name: string }>).some(e => e.name === this.state.params.tab)
    if (!pageExists) this.state.params.tab = 'home'
  },

  writeQueryString() {
    const paramsObject = Object.fromEntries(new URLSearchParams(window.location.search).entries())
    const stateKeys = Object.keys(this.state.params)

    const valuesAreEqual = stateKeys.every(key => String(paramsObject[key]) === String(this.state.params[key]))

    if (!valuesAreEqual) {
      const url = new URL(window.location.href)
      const params = new URLSearchParams(
        Object.entries({
          ...paramsObject,
          ...Object.fromEntries(Object.entries(this.state.params).map(([k, v]) => [k, String(v)]))
        })
      )

      url.search = params.toString()
      window.history.pushState(toRaw(this.state.params), '', url.toString())
      this.updateLocation()
    }
  },

  changeTab(tab: string) {
    const pageObj = (PAGES as Array<{ name: string; smartbook?: boolean; floatingNav?: boolean }>).find(
      e => e.name === tab
    )

    if (!pageObj && tab !== 'home') {
      console.warn('Tried to change to a nonexistent tab:', tab)
      this.state.params.tab = 'home'
    } else if (pageObj?.smartbook && !this.state.smartbookAvailable) {
      console.warn('Tried to open smartbook tab, but smartbook is not available. Tab:', tab)
      this.state.params.tab = 'home'
    } else {
      this.state.params.tab = tab
    }

    const willFloat =
      (PAGES as Array<{ name: string; floatingNav?: boolean }>).find(e => e.name === tab)?.floatingNav ?? false
    this.state.params.hidden = willFloat

    this.writeQueryString()
  },

  toggleHidden() {
    this.state.params.hidden = !this.state.params.hidden
    this.writeQueryString()
  }
})

interface GetLinkOptions {
  href?: string
  tab?: string
  hidden?: string
  hash?: string
  unitId?: number
}

export function useNavigation() {
  const navigationState = navigationStore.state

  function getLink({ href, tab, hidden, hash, unitId }: GetLinkOptions): string {
    const url = new URL(href ?? navigationState.currentLocation)

    if (tab) url.searchParams.set('tab', tab)
    if (hidden) url.searchParams.set('hidden', hidden)

    if (hash) url.hash = hash
    else url.hash = ''

    if (unitId) {
      const path = url.pathname.split('/')
      path.pop()
      path.push(unitId.toString())
      url.pathname = path.join('/')
    }

    return url.toString()
  }

  function goto(href: string) {
    const url = new URL(href)
    const unitId = Number(url.pathname.split('/').pop())
    const currentUnitId = Number(new URL(navigationState.currentLocation).pathname.split('/').pop())

    if (unitId !== currentUnitId) {
      window.location.href = href
    } else {
      window.location.hash = url.hash
      navigationStore.changeTab(url.searchParams.get('tab') || 'home')
    }
  }

  function gotoOnClick(event: Event) {
    event.preventDefault()
    const href = (event.currentTarget as HTMLAnchorElement).href
    if (href) return goto(href)
  }

  return {
    navigationState,
    navigationStore,
    getLink,
    goto,
    gotoOnClick
  }
}
